import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/ui/select';
import { Loader2, Paperclip, X, ChevronDown, ChevronUp } from 'lucide-react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { AddNewTradeDialog } from '@/shared/forms';
import {
  useObservationMutations, useObservationTypes, useCreateObservationType,
} from '../hooks';
import { useTrades, useCreateTrade, useProjectUsers } from '@/features/punch-lists/hooks';
import type { CreateObservationData, Observation, ObservationPriority, UserMinimal } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | undefined;
  item?: Observation | null;
}

const PRIORITY_OPTIONS: { value: ObservationPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

type FormState = Omit<CreateObservationData, 'project' | 'hotspot'>;

const INITIAL_FORM: FormState = {
  title: '',
  priority: 'MEDIUM',
  status: 'OPEN',
  description: '',
  is_private: false,
  due_date: '',
  notification_date: '',
  spec_section: '',
  assignee: null,
  observation_type: null,
  trade: null,
  location: null,
  distribution_list: [],
  hazard: '',
  contributing_behavior: '',
  contributing_condition: '',
};

const userDisplayName = (u: UserMinimal) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

// AddNewObservationTypeDialog - inline since it's simple
const AddNewObservationTypeDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onConfirm: (value: string) => void;
}> = ({ open, onOpenChange, searchValue, onConfirm }) => {
  const [name, setName] = useState(searchValue);
  React.useEffect(() => { if (open) setName(searchValue); }, [open, searchValue]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>New Observation Type</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); if (name.trim()) onConfirm(name.trim()); }} className="space-y-4 pt-1">
          <div className="space-y-1">
            <Label htmlFor="new-obs-type-name">Name</Label>
            <Input id="new-obs-type-name" placeholder="e.g. Safety Walk" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!name.trim()}>Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ObservationCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  projectId,
  item,
}) => {
  const { createItem, updateItem, uploadAttachment } = useObservationMutations(projectId);
  const isEdit = !!item;
  const { data: observationTypes } = useObservationTypes();
  const { data: trades } = useTrades();
  const { data: projectUsers } = useProjectUsers(projectId);
  const createObservationType = useCreateObservationType();
  const createTrade = useCreateTrade();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSafety, setShowSafety] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    if (!item) {
      setForm(INITIAL_FORM);
      return;
    }
    setForm({
      title: item.title,
      priority: item.priority,
      status: item.status,
      description: item.description ?? '',
      is_private: item.is_private,
      due_date: item.due_date?.slice(0, 16) ?? '',
      notification_date: item.notification_date?.slice(0, 16) ?? '',
      spec_section: item.spec_section ?? '',
      assignee: item.assignee?.id ?? null,
      observation_type: item.observation_type?.id ?? null,
      trade: item.trade?.id ?? null,
      location: item.location?.id ?? null,
      distribution_list: item.distribution_list.map((user) => user.id),
      hazard: item.hazard ?? '',
      contributing_behavior: item.contributing_behavior ?? '',
      contributing_condition: item.contributing_condition ?? '',
    });
  }, [item, open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setNullableUser = (key: 'assignee', raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === 'none' ? null : Number(raw) }));

  const toggleDistribution = (userId: number) => {
    setForm((prev) => {
      const current = prev.distribution_list as number[];
      return {
        ...prev,
        distribution_list: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (index: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));

  const handleAddNewType = async (name: string) => {
    const newType = await createObservationType.mutateAsync(name);
    set('observation_type', newType.id);
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    set('trade', newTrade.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setError(null);
    try {
      const payload: CreateObservationData = {
        ...form,
        project: projectId ?? 0,
        due_date: form.due_date || null,
        notification_date: form.notification_date || null,
        assignee: form.assignee || null,
        observation_type: form.observation_type || null,
        trade: form.trade || null,
        location: form.location || null,
      };
      const savedItem = isEdit && item
        ? await updateItem.mutateAsync({ id: item.id, data: payload })
        : await createItem.mutateAsync(payload);
      for (const file of pendingFiles) {
        await uploadAttachment.mutateAsync({ observationId: savedItem.id, file });
      }
      setForm(INITIAL_FORM);
      setPendingFiles([]);
      setShowSafety(false);
      onSuccess?.();
      onClose();
    } catch {
      setError(`Failed to ${isEdit ? 'update' : 'create'} observation. Please try again.`);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setPendingFiles([]);
    setError(null);
    setShowSafety(false);
    onClose();
  };

  const distList = (form.distribution_list as number[]) ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Observation' : 'Create Observation'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'Update this observation.' : 'Fill in the details below to create a new observation.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">

          {/* Title */}
          <div className="space-y-1">
            <Label htmlFor="obs-title">Title <span className="text-destructive">*</span></Label>
            <Input id="obs-title" placeholder="Describe the observation" value={form.title} onChange={(e) => set('title', e.target.value)} />
          </div>

          {/* Priority + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v as ObservationPriority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <CustomCombobox
                options={(observationTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={form.observation_type ?? ''}
                onChange={(v) => set('observation_type', v || null)}
                placeholder="Select type"
                searchPlaceholder="Search types…"
                addNewLabel="Add New Type"
                emptyMessage="No types found."
                onAddNew={handleAddNewType}
                AddNewDialog={AddNewObservationTypeDialog}
              />
            </div>
          </div>

          {/* Assignee */}
          <div className="space-y-1">
            <Label>Assignee</Label>
            <Select value={form.assignee != null ? String(form.assignee) : 'none'} onValueChange={(v) => setNullableUser('assignee', v)}>
              <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(projectUsers ?? []).map((u: UserMinimal) => (<SelectItem key={u.id} value={String(u.id)}>{userDisplayName(u)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date + Notification Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="obs-due-date">Due Date</Label>
              <Input id="obs-due-date" type="datetime-local" value={form.due_date ?? ''} onChange={(e) => {
                const val = e.target.value;
                if (!val) { set('due_date', val); return; }
                if (!form.due_date) { set('due_date', `${val.slice(0, 10)}T23:59`); } else { set('due_date', val); }
              }} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="obs-notif-date">Notification Date</Label>
              <Input id="obs-notif-date" type="datetime-local" value={form.notification_date ?? ''} onChange={(e) => set('notification_date', e.target.value)} />
            </div>
          </div>

          {/* Trade + Spec Section */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Trade</Label>
              <CustomCombobox
                options={(trades ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={form.trade ?? ''}
                onChange={(v) => set('trade', v || null)}
                placeholder="Select trade"
                searchPlaceholder="Search trades…"
                addNewLabel="Add New Trade"
                emptyMessage="No trades found."
                onAddNew={handleAddNewTrade}
                AddNewDialog={AddNewTradeDialog}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="obs-spec">Spec Section</Label>
              <Input id="obs-spec" placeholder="e.g. 03 30 00" value={form.spec_section ?? ''} onChange={(e) => set('spec_section', e.target.value)} />
            </div>
          </div>

          {/* Distribution List */}
          <div className="space-y-2">
            <Label>Distribution List</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
              {(projectUsers ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No users available</p>
              ) : (projectUsers ?? []).map((u: UserMinimal) => (
                <div key={u.id} className="flex items-center gap-2">
                  <Checkbox id={`obs-dist-${u.id}`} checked={distList.includes(u.id)} onCheckedChange={() => toggleDistribution(u.id)} />
                  <label htmlFor={`obs-dist-${u.id}`} className="text-sm cursor-pointer">{userDisplayName(u)}</label>
                </div>
              ))}
            </div>
            {distList.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {distList.map((id) => {
                  const u = (projectUsers ?? []).find((u: UserMinimal) => u.id === id);
                  return u ? (<Badge key={id} variant="secondary" className="gap-1">{userDisplayName(u)}<X className="h-3 w-3 cursor-pointer" onClick={() => toggleDistribution(id)} /></Badge>) : null;
                })}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="obs-description">Description</Label>
            <Textarea id="obs-description" placeholder="Describe what was observed" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>

          {/* Safety / Quality (collapsible) */}
          <div className="border rounded-lg">
            <button type="button" className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-accent transition-colors rounded-lg" onClick={() => setShowSafety(!showSafety)}>
              Safety / Quality Details
              {showSafety ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showSafety && (
              <div className="px-3 pb-3 space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="obs-hazard">Hazard</Label>
                  <Textarea id="obs-hazard" placeholder="Describe the hazard" rows={2} value={form.hazard} onChange={(e) => set('hazard', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="obs-behavior">Contributing Behavior</Label>
                  <Textarea id="obs-behavior" placeholder="Describe contributing behavior" rows={2} value={form.contributing_behavior} onChange={(e) => set('contributing_behavior', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="obs-condition">Contributing Condition</Label>
                  <Textarea id="obs-condition" placeholder="Describe contributing condition" rows={2} value={form.contributing_condition} onChange={(e) => set('contributing_condition', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label>Attachments</Label>
            <div>
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => fileInputRef.current?.click()}>
                <Paperclip className="h-4 w-4" /> Attach Files
              </Button>
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
            </div>
            {pendingFiles.length > 0 && (
              <ul className="space-y-1">
                {pendingFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm border rounded px-2 py-1">
                    <span className="truncate max-w-xs">{f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="ml-2 text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Private */}
          <div className="flex items-center gap-2">
            <Checkbox id="obs-private" checked={form.is_private} onCheckedChange={(checked) => set('is_private', checked === true)} />
            <Label htmlFor="obs-private" className="cursor-pointer">Private — only visible to creator, assignee, and distribution list</Label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
              {(createItem.isPending || updateItem.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Observation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
